/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Module-level handle that lets a connection-error overlay's Retry button wake
 * a CRITICAL retry loop that is already backing off.
 *
 * The blocking service calls sleep between attempts inside `RetryMechanism`. A
 * manual Retry must collapse that sleep and re-issue the *same* call
 * immediately, without starting a second request. It does that by aborting the
 * current "retry now" controller: any backoff wait listening on its signal
 * resolves early and the loop re-attempts at once. A wake while a request is
 * already in flight is a no-op — no wait is listening — so no duplicate request
 * can be created.
 *
 * A fresh controller is installed on every trigger so a past abort can never
 * short-circuit a later backoff into a tight loop. Every CRITICAL overlay reads
 * {@link getRetryNowSignal} fresh before each wait through its retry options,
 * and the overlay bridge calls {@link triggerManualRetry} on press. A single
 * shared controller is correct: the blocking overlays are shown one at a time,
 * and where several loops do run at once waking all of them is the intent.
 */

let retryNowController: AbortController = new AbortController();

/** The signal a backoff wait must listen on to be wakeable by a manual retry. */
export function getRetryNowSignal(): AbortSignal {
  return retryNowController.signal;
}

/**
 * Wakes the loop currently sleeping between CRITICAL attempts so it retries
 * now. Installs a fresh controller first, then aborts the previous one, so the
 * next backoff arms a clean, un-aborted signal.
 */
export function triggerManualRetry(): void {
  const current: AbortController = retryNowController;
  retryNowController = new AbortController();
  current.abort();
}

/**
 * Discards any pending wake so the next attempt starts from a clean
 * controller. Used during Exit cleanup.
 */
export function resetManualRetry(): void {
  retryNowController = new AbortController();
}
